import React, { useState } from 'react';
import { t as uiText } from '../i18n';
import { useWorkspaceEntitlements } from '../features/plans/useWorkspaceEntitlements';
import { findUpgradePlan, type UpgradeLimitKind } from '../features/plans/planUpgrade';
import UpgradeModal from './UpgradeModal';

type Props = {
  /** どの上限か。誘導先のプランを決めるのに使う */
  kind: UpgradeLimitKind;
  title: string;
  message: string;
  /** 例: 「現在のメンバー数: 3 / 3人」 */
  usageLabel?: string;
  /** プラン変更できる権限（owner / admin）があるか */
  isAdmin: boolean;
  /** 使用量に合わせた補助ボタン（例: メンバーを管理する） */
  extraActions?: React.ReactNode;
  tone?: 'error' | 'warning';
};

const TONES = {
  error: { border: '#e0a4a0', background: '#fdf1f0', title: '#b3261e' },
  warning: { border: '#f0c9a4', background: '#fff7ed', title: '#8a4b12' },
} as const;

/**
 * プランの上限に達したときの通知。上限が実際に増えるプランがあるときだけ、
 * 既存の UpgradeModal を開くボタンを出す。ないときは理由だけ伝える。
 */
const PlanLimitNotice: React.FC<Props> = ({
  kind,
  title,
  message,
  usageLabel,
  isAdmin,
  extraActions,
  tone = 'error',
}) => {
  const { effective } = useWorkspaceEntitlements();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const target = isAdmin ? findUpgradePlan(effective, kind) : null;
  const colors = TONES[tone];

  let hint: string | null = null;
  if (!isAdmin) {
    hint = uiText('Please ask a workspace administrator to change the plan.');
  } else if (!target) {
    hint = uiText('No larger plan is available right now.');
  }

  return (
    <div
      role="alert"
      style={{
        marginTop: 12,
        padding: '12px 14px',
        borderRadius: 8,
        border: `1px solid ${colors.border}`,
        background: colors.background,
        textAlign: 'left',
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 14, color: colors.title, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6 }}>{message}</div>
      {usageLabel && (
        <div style={{ fontSize: 13, color: '#333', fontWeight: 600, marginTop: 8 }}>{usageLabel}</div>
      )}
      {hint && <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>{hint}</div>}
      {(target || extraActions) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {target && (
            <button
              type="button"
              onClick={() => setUpgradeOpen(true)}
              style={{
                padding: '8px 14px',
                border: 'none',
                borderRadius: 6,
                background: 'var(--workspace-primary, #cfae91)',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {uiText('Review or change plan')}
            </button>
          )}
          {extraActions}
        </div>
      )}
      {upgradeOpen && (
        <UpgradeModal
          isOpen
          onClose={() => setUpgradeOpen(false)}
          highlightPlanId={target as 'starter' | null}
          reason={message}
        />
      )}
    </div>
  );
};

export default PlanLimitNotice;
